/*
 * ISG personaliser — order one or more designs across several colours and sizes.
 * Original artwork files are sent unchanged to Shopify as line item property uploads
 * (once per design; other lines of the same design reference the stored file).
 * Prices shown here are for display; Shopify charges the real variant prices at checkout.
 */
(function (root) {
  'use strict';

  /* ---------- Pure helpers (unit-tested in Node) ---------- */

  function money(pence, currency) {
    var symbol = currency === 'GBP' || !currency ? '£' : currency + ' ';
    return symbol + (Math.round(pence) / 100).toFixed(2);
  }

  function round1(n) {
    return Math.round(n * 10) / 10;
  }

  // Largest print that keeps the artwork's proportions and fits the size tier and the print area.
  function fitToTier(aspect, tier, area) {
    var maxW = Math.min(tier.maxW, area.w);
    var maxH = Math.min(tier.maxH, area.h);
    var w = Math.min(maxW, maxH * aspect);
    return { w: w, h: w / aspect };
  }

  function clampBox(box, area) {
    var x = Math.min(Math.max(box.x, 0), Math.max(area.w - box.w, 0));
    var y = Math.min(Math.max(box.y, 0), Math.max(area.h - box.h, 0));
    return { x: x, y: y, w: box.w, h: box.h };
  }

  // Keep a box inside bounds { x0, y0, x1, y1 } (used to keep prints on the product photo).
  function clampToBounds(box, b) {
    var x = Math.min(Math.max(box.x, b.x0), Math.max(b.x1 - box.w, b.x0));
    var y = Math.min(Math.max(box.y, b.y0), Math.max(b.y1 - box.h, b.y0));
    return { x: x, y: y, w: box.w, h: box.h };
  }

  // The whole product photo in cm, relative to the top-left of the side's print area.
  // The print area is calibrated to the photo (zone %), so the photo's size in cm follows from it.
  function stageBounds(side, stageAspect) {
    if (!side.zoneWidth || !stageAspect) return { x0: 0, y0: 0, x1: side.areaW, y1: side.areaH };
    var wCm = side.areaW / (side.zoneWidth / 100), hCm = wCm / stageAspect;
    var x0 = -(side.zoneLeft / 100) * wCm, y0 = -(side.zoneTop / 100) * hCm;
    return { x0: x0, y0: y0, x1: x0 + wCm, y1: y0 + hCm };
  }

  /*
   * Long sleeves hanging beside the body: on rows where an arm hangs clear of the body on both sides (garment, then
   * background, then the body), the run through the middle is the body alone. Returns that run [left, right] in px,
   * taken from the highest such rows (nearest the chest), or null when the arms never hang clear (fewer than 2 rows).
   * isFg(x, y) says whether a pixel is garment. Rows from 36% to 92% of the garment height (above the hem rib).
   */
  function splitBody(isFg, w, top, gh, cx) {
    var rows = [];
    for (var k = 18; k <= 46; k++) {
      var yy = Math.floor(top + gh * k / 50);
      if (!isFg(cx, yy)) continue;
      var a = cx, b = cx, x, nl = 0, nr = 0;
      while (a > 0 && isFg(a - 1, yy)) a--;
      while (b < w - 1 && isFg(b + 1, yy)) b++;
      for (x = 0; x < a - 1; x++) if (isFg(x, yy)) nl++;
      for (x = b + 2; x < w; x++) if (isFg(x, yy)) nr++;
      // a real arm, not a stray speck or shadow: a few garment pixels on each side
      if (nl >= Math.max(3, w * 0.012) && nr >= Math.max(3, w * 0.012)) rows.push([a, b]);
    }
    if (rows.length < 2) return null;
    var hi = rows.slice(0, 3).sort(function (p, q) { return (p[1] - p[0]) - (q[1] - q[0]); });
    return hi[Math.floor(hi.length / 2)];
  }

  /*
   * Measure a flat garment photo (RGBA pixels, plain background): the body width between the side
   * seams at mid height, the top of the garment and its outline. Returns the print area as % of the
   * photo (zoneLeft/zoneWidth = body width, zoneTop = just below the collar) or null if unsure.
   */
  function garmentFit(data, w, h, opts) {
    function px(x, y) { var i = (y * w + x) * 4; return [data[i], data[i + 1], data[i + 2]]; }
    var cs = [px(2, 2), px(w - 3, 2), px(2, h - 3), px(w - 3, h - 3)];
    var bg = [0, 1, 2].map(function (k) { var v = cs.map(function (c) { return c[k]; }).sort(function (a, b) { return a - b; }); return (v[1] + v[2]) / 2; });
    function fg(x, y) { var c = px(x, y); return Math.abs(c[0] - bg[0]) + Math.abs(c[1] - bg[1]) + Math.abs(c[2] - bg[2]) > 36; }
    var top = -1, bot = -1, left = w, right = -1;
    for (var y = 0; y < h; y++) {
      var n = 0, l = -1, r = -1;
      for (var x = 0; x < w; x++) if (fg(x, y)) { n++; if (l < 0) l = x; r = x; }
      if (n > 2) { if (top < 0) top = y; bot = y; if (l < left) left = l; if (r > right) right = r; }
    }
    if (top < 0 || bot - top < h * 0.3) return null;
    var gh = bot - top, cx = Math.floor(w / 2);
    // Body width (side seam to side seam): the middle of several rows across the body.
    function bodyAt(rows) {
      var runs = [];
      rows.forEach(function (f) {
        var yy = Math.floor(top + gh * f);
        if (!fg(cx, yy)) return;
        var a = cx, b = cx;
        while (a > 0 && fg(a - 1, yy)) a--;
        while (b < w - 1 && fg(b + 1, yy)) b++;
        runs.push([a, b]);
      });
      if (runs.length < 3) return null;
      runs.sort(function (a, b) { return (a[1] - a[0]) - (b[1] - b[0]); });
      var mm = runs[Math.floor(runs.length / 2)], bww = (mm[1] - mm[0] + 1) / w;
      if (bww < 0.2 || bww > 0.9) return null;
      // The body is always a good share of the whole outline (sleeves included); much less means the body edge was lost,
      // e.g. speckled marl fabric on a white background, so don't trust it.
      if (bww < 0.4 * (right + 1 - left) / w) return null;
      // Product photos are centred, so a body well off the middle means one edge was lost (white on white, two-tone raglans).
      if (Math.abs((mm[0] + mm[1] + 1) / 2 / w - 0.5) > 0.05) return null;
      return { m: mm, bw: bww };
    }
    // Mid-height first; dropped-shoulder and oversized tees have sleeves hanging that low, so then try lower down.
    var body = bodyAt([0.45, 0.5, 0.55, 0.6, 0.65]) || bodyAt([0.68, 0.72, 0.76, 0.8, 0.84]);
    if (!body) return null;
    var m = body.m, bw = body.bw;
    // Long sleeves hanging against the body make the middle run take in the arms. Where the arms hang clear lower down,
    // that body-only run is used when it is clearly narrower; if they never hang clear and the run is nearly the whole
    // outline, the arms can't be told from the body, so the photo isn't trusted (another colour's measurement is used).
    // That last check only applies to long-sleeved products (opts.longSleeve): a vest's body is its whole outline.
    var sp = splitBody(fg, w, top, gh, cx), mw = m[1] - m[0] + 1;
    if (sp && sp[1] - sp[0] + 1 < mw * 0.93) {
      var sw = (sp[1] - sp[0] + 1) / w;
      if (sw >= 0.2 && sw >= 0.4 * (right + 1 - left) / w && Math.abs((sp[0] + sp[1] + 1) / 2 / w - 0.5) <= 0.05) { m = sp; bw = sw; }
      // an unlikely body-only run (e.g. pale stripes or white on white breaking the row) is ignored, except on long sleeves
      else if (opts && opts.longSleeve) return null;
    } else if (!sp && opts && opts.longSleeve && mw >= 0.85 * (right + 1 - left)) return null;
    // How light the garment is (0 dark – 255 light), sampled on the chest; used to pick a text colour that shows up.
    var sum = 0, cnt = 0, ty = Math.floor(top + gh * 0.4);
    for (var dy = -2; dy <= 2; dy++) for (var dx = -2; dx <= 2; dx++) {
      var c = px(Math.min(w - 1, Math.max(0, cx + dx * 3)), Math.min(h - 1, Math.max(0, ty + dy * 3)));
      sum += 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]; cnt++;
    }
    // The print area starts just below the collar. On hoodies (opts.hood) the photo's top is the hood, so it starts a
    // little below the shoulders instead: the first row where the outline is nearly as wide as the body.
    var zt = top + gh * 0.10;
    if (opts && opts.hood) {
      for (var yh = top; yh < bot; yh++) {
        var l2 = -1, r2 = -1;
        for (var xh = 0; xh < w; xh++) if (fg(xh, yh)) { if (l2 < 0) l2 = xh; r2 = xh; }
        if (l2 >= 0 && r2 - l2 + 1 >= 0.9 * bw * w) { zt = yh + (bot - yh) * 0.12; break; }
      }
    }
    return { zoneLeft: m[0] / w * 100, zoneWidth: bw * 100, zoneTop: zt / h * 100, tone: sum / cnt,
             box: { l: left / w * 100, t: top / h * 100, r: (right + 1) / w * 100, b: (bot + 1) / h * 100 } };
  }

  // "33 x 36", "33×36 cm", "33 X 36cm" -> { w: 33, h: 36 }; null if it isn't two positive numbers.
  function parseCmPair(text) {
    var m = String(text == null ? '' : text).replace(/,/g, '.').match(/(\d+(?:\.\d+)?)\s*(?:cm)?\s*[x×*by]+\s*(\d+(?:\.\d+)?)/i);
    if (!m) return null;
    var w = parseFloat(m[1]), h = parseFloat(m[2]);
    return w > 0 && h > 0 ? { w: w, h: h } : null;
  }

  // Per-size values: "S: 10.5 x 7, M: 16 x 12, L: 20 x 15" -> { S: {w,h}, M: {w,h}, L: {w,h} } (size names upper-cased);
  // a single "33 x 36" -> null (use parseCmPair). Sizes that don't parse are skipped.
  function parseSizedPairs(text) {
    var parts = String(text == null ? '' : text).split(/[;\n]|,(?=\s*[A-Za-z0-9\/]+\s*:)/), out = {}, any = false;
    parts.forEach(function (p) {
      var m = p.match(/^\s*([^:]+?)\s*:\s*(.+)$/);
      if (!m) return;
      var v = parseCmPair(m[2]);
      if (v) { out[m[1].trim().toUpperCase()] = v; any = true; }
    });
    return any ? out : null;
  }

  /*
   * Place a bag's print area on its body. body: the bag body in px ({ l, t, r, b }, handles excluded) on a w × h photo;
   * bag: the bag's front size in cm; area: the print area in cm. The print area is centred across the body; the scale comes
   * from the body width = the bag width. vpos (0–1, default 0.5 = centred) says how far down the body the print area sits
   * (backpacks: lower, over the front pocket). Returns the zone in % of the photo plus the body box (prints stay on the bag).
   */
  function bagPlace(body, w, h, bag, area, vpos) {
    var bw = body.r - body.l, bh = body.b - body.t;
    var pxcm = bw / bag.w, aw = area.w * pxcm, ah = Math.min(area.h * pxcm, bh);
    var v = vpos == null ? 0.5 : Math.min(1, Math.max(0, vpos));
    var cx = (body.l + body.r) / 2, top = body.t + Math.max(0, (bh - ah) * v);
    return { zoneLeft: (cx - aw / 2) / w * 100, zoneWidth: aw / w * 100, zoneTop: top / h * 100,
             box: { l: body.l / w * 100, t: body.t / h * 100, r: body.r / w * 100, b: body.b / h * 100 } };
  }

  /*
   * A bag end with no photo (holdall and barrel ends): the end panel is drawn to scale, centred on a W × H stage (a little
   * above the middle, clear of the caption) and as big as fits 62% of the stage each way. end: the end panel in cm; area: its
   * print area in cm (never bigger than the panel). Returns the print area zone, the panel box (both in % of the stage).
   */
  function endPlace(W, H, end, area) {
    var s = Math.min(0.62 * W / end.w, 0.62 * H / end.h), ew = end.w * s, eh = end.h * s;
    var l = (W - ew) / 2, t = Math.max(0, (H * 0.92 - eh) / 2);
    var out = bagPlace({ l: l, t: t, r: l + ew, b: t + eh }, W, H, end, { w: Math.min(area.w, end.w), h: Math.min(area.h, end.h) }, 0.5);
    out.endPanel = true;
    return out;
  }

  /*
   * Find the body of a bag in a flat packshot (RGBA pixels, plain background), handles and straps excluded: the rows
   * where the bag runs nearly full width through the middle of the photo. Returns { l, t, r, b } in px and the fabric
   * tone, or { fail: reason } when unsure — no clear body, off-centre, or slanted sides (an angled photo).
   */
  function bagBody(data, w, h) {
    function px(x, y) { var i = (y * w + x) * 4; return [data[i], data[i + 1], data[i + 2]]; }
    var cs = [px(2, 2), px(w - 3, 2), px(2, h - 3), px(w - 3, h - 3)];
    var bg = [0, 1, 2].map(function (k) { var v = cs.map(function (c) { return c[k]; }).sort(function (a, b) { return a - b; }); return (v[1] + v[2]) / 2; });
    function fg(x, y) { var c = px(x, y); return Math.abs(c[0] - bg[0]) + Math.abs(c[1] - bg[1]) + Math.abs(c[2] - bg[2]) > 36; }
    // The bag's extent across each row, through the middle of the photo. Small gaps are stepped over: a white swing
    // tag, a pale stripe or a seam is not the edge of the bag.
    var cx = Math.floor(w / 2), gap = Math.max(2, Math.round(w * 0.1)), runs = [], maxRun = 0, y, x;
    for (y = 0; y < h; y++) {
      var start = -1;
      for (var o = 0; o <= gap && start < 0; o++) { if (fg(cx - o, y)) start = cx - o; else if (fg(cx + o, y)) start = cx + o; }
      if (start < 0) { runs.push(null); continue; }
      var a = start, b = start, miss;
      for (x = a - 1, miss = 0; x >= 0 && miss <= gap; x--) { if (fg(x, y)) { a = x; miss = 0; } else miss++; }
      for (x = b + 1, miss = 0; x < w && miss <= gap; x++) { if (fg(x, y)) { b = x; miss = 0; } else miss++; }
      runs.push([a, b]);
      if (b - a + 1 > maxRun) maxRun = b - a + 1;
    }
    if (maxRun < 0.25 * w) return { fail: 'no body' };
    function wide(y, f) { var r = runs[y]; return r && r[1] - r[0] + 1 >= f * maxRun; }
    // Top of the body: the first row of a run of 4 nearly-full-width rows (handles and straps are much narrower).
    var top = -1;
    for (y = 0; y < h - 4 && top < 0; y++) if (wide(y, 0.8) && wide(y + 1, 0.8) && wide(y + 2, 0.8) && wide(y + 3, 0.8)) top = y;
    if (top < 0) return { fail: 'no body' };
    // Bottom: carry on while the bag is still at least half as wide (cut-corner and rounded bases narrow a little),
    // stepping over a few pale rows (a light stripe) if the bag carries on below them.
    var bot = top, rowGap = Math.max(2, Math.round(h * 0.05));
    for (y = top + 1; y < h && y - bot <= rowGap; y++) if (wide(y, 0.5)) bot = y;
    var bh = bot - top + 1;
    if (bh < 0.2 * h) return { fail: 'body too short' };
    // Width, centre and how straight the sides are, from the upper-middle of the body (clear of the base corners).
    var ls = [], rs = [];
    for (y = Math.floor(top + bh * 0.1); y <= Math.floor(top + bh * 0.6); y++) if (runs[y]) { ls.push(runs[y][0]); rs.push(runs[y][1]); }
    if (ls.length < 5) return { fail: 'no body' };
    var l = median(ls), r = median(rs) + 1;
    var spread = Math.max(Math.max.apply(null, ls) - Math.min.apply(null, ls), Math.max.apply(null, rs) - Math.min.apply(null, rs));
    // Slanted sides mean an angled photo: still usable as a rough guide, marked approximate.
    var approx = spread > 0.04 * w;
    if (Math.abs((l + r) / 2 / w - 0.5) > (approx ? 0.08 : 0.05)) return { fail: 'off centre' };
    var sum = 0, cnt = 0, ty = Math.floor(top + bh * 0.4), mx = Math.round((l + r) / 2);
    for (var dy = -2; dy <= 2; dy++) for (var dx = -2; dx <= 2; dx++) {
      var c = px(Math.min(w - 1, Math.max(0, mx + dx * 3)), Math.min(h - 1, Math.max(0, ty + dy * 3)));
      sum += 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]; cnt++;
    }
    return { l: l, t: top, r: r, b: bot + 1, tone: sum / cnt, approx: approx };
  }

  /*
   * Caps and beanies (packshots on a plain background): find the whole product outline, then place the print area on it.
   * kind 'cap': on the front panel above the peak. Caps are shot from the front at an angle, so the peak points left or
   * right; the print area moves a little towards the peak side. kind 'beanie': centred, just above the bottom edge (the
   * cuff). kind 'visor': like a cap but higher, on the front band. kind 'bucket' (bucket and brimmed hats): centred on
   * the crown above the brim. bodyCm: how many cm the outline width stands for (sets the scale). Pale grey drop shadows
   * are ignored. Returns
   * { zoneLeft, zoneTop, zoneWidth, box } in % of the photo, or null when the outline isn't a believable cap or beanie
   * (white on white, cut off): the template position or another colour's photo is used instead.
   */
  function headwearFit(data, w, h, kind, areaW, areaH, bodyCm) {
    if (!areaW || !areaH || !bodyCm) return null;
    function px(x, y) { var i = (y * w + x) * 4; return [data[i], data[i + 1], data[i + 2]]; }
    var cs = [px(2, 2), px(w - 3, 2), px(2, h - 3), px(w - 3, h - 3)];
    var bg = [0, 1, 2].map(function (k) { var v = cs.map(function (c) { return c[k]; }).sort(function (a, b) { return a - b; }); return (v[1] + v[2]) / 2; });
    var rows = new Array(h), cols = new Array(w), x, y;
    for (y = 0; y < h; y++) rows[y] = 0;
    for (x = 0; x < w; x++) cols[x] = 0;
    var mask = new Uint8Array(w * h), onN = 0, paleN = 0;
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) {
      var c = px(x, y), mx = Math.max(c[0], c[1], c[2]), mn = Math.min(c[0], c[1], c[2]);
      var lum = 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2];
      var dif = Math.abs(c[0] - bg[0]) + Math.abs(c[1] - bg[1]) + Math.abs(c[2] - bg[2]);
      var on = dif > 36 && !(mx - mn < 18 && lum > 160);
      if (on) { mask[y * w + x] = 1; rows[y]++; cols[x]++; onN++; } else if (mx - mn < 18 && lum > 160 && dif > 12) paleN++;
    }
    var t = -1, b = -1, l = -1, r = -1, k = Math.max(1, Math.round(w / 150));
    for (y = 0; y < h; y++) if (rows[y] > k) { if (t < 0) t = y; b = y; }
    for (x = 0; x < w; x++) if (cols[x] > k) { if (l < 0) l = x; r = x; }
    if (t < 0 || l < 0) return null;
    var ww = r - l + 1, hh = b - t + 1, asp = hh / ww;
    if (ww < 0.4 * w) return null;
    var lim = { cap: [0.5, 0.95], beanie: [0.7, 1.45], visor: [0.2, 0.62], bucket: [0.3, 1.0] }[kind];
    if (!lim || asp < lim[0] || asp > lim[1]) return null;
    // A white visor loses its band against the white background (only the darker trim is left), so a visor photo that
    // is mostly pale product is not trusted.
    if (kind === 'visor' && paleN > 0.85 * (onN + paleN)) return null;
    var zw = ww * areaW / bodyCm, zh = zw * areaH / areaW, cx, top;
    if (kind === 'cap' || kind === 'visor') {
      // Which way the peak points: where the bottom sixth of the outline sits. A visor has no crown, so its print area
      // sits higher, on the front band.
      var sx = 0, n = 0, y0 = b - Math.floor(hh / 6);
      for (y = y0; y <= b; y++) for (x = l; x <= r; x++) if (mask[y * w + x]) { sx += x; n++; }
      var low = n ? (sx / n - l) / ww : 0.5;
      cx = l + ww * (low < 0.45 ? 0.47 : low > 0.55 ? 0.53 : 0.5);
      top = t + hh * (kind === 'cap' ? 0.16 : 0.1);
    } else if (kind === 'bucket') {
      // Bucket and brimmed hats: the brim starts at the first row nearly as wide as the whole hat; the print area is
      // centred on the crown above it (a chin cord hanging below doesn't count).
      var maxRow = 0, bt = -1;
      for (y = t; y <= b; y++) if (rows[y] > maxRow) maxRow = rows[y];
      for (y = t; y <= b && bt < 0; y++) if (rows[y] >= 0.85 * maxRow) bt = y;
      var crown = bt - t;
      if (crown < 0.15 * ww) return null;
      var sx2 = 0, n2 = 0;
      for (y = t; y < bt; y++) for (x = l; x <= r; x++) if (mask[y * w + x]) { sx2 += x; n2++; }
      cx = n2 ? sx2 / n2 : l + ww / 2;
      if (Math.abs(cx - (l + ww / 2)) > 0.08 * ww) return null;
      top = Math.max(t + crown * 0.12, Math.min(bt - zh - crown * 0.05, t + crown * 0.55 - zh / 2));
    } else {
      cx = l + ww * 0.5;
      top = b + 1 - hh * 0.09 - zh;
    }
    return { zoneLeft: (cx - zw / 2) / w * 100, zoneTop: top / h * 100, zoneWidth: zw / w * 100,
      box: { l: l / w * 100, t: t / h * 100, r: (r + 1) / w * 100, b: (b + 1) / h * 100 } };
  }

  /*
   * Trousers and shorts (flat front packshots on a plain background): a print area on each leg, just below the crotch.
   * Finds the outline, the waistband width (which stands for waistCm and sets the scale), the crotch (the gap between
   * the legs, followed up the middle from the hem) and each leg a little below it. Returns
   * { zoneLeft, zoneTop, zoneWidth, box, tone, legs: { left, right } } in % of the photo, where legs.left is the wearer's
   * left leg (on the right of a front photo) and the top-level zone is that leg; or null when the photo isn't a believable
   * flat front view (side-on or angled, a skirt, cut off, white on white): another colour's photo or the template is used.
   */
  function legFit(data, w, h, areaW, areaH, waistCm) {
    if (!areaW || !areaH || !waistCm) return null;
    function px(x, y) { var i = (y * w + x) * 4; return [data[i], data[i + 1], data[i + 2]]; }
    var cs = [px(2, 2), px(w - 3, 2), px(2, h - 3), px(w - 3, h - 3)];
    var bg = [0, 1, 2].map(function (k) { var v = cs.map(function (c) { return c[k]; }).sort(function (a, b) { return a - b; }); return (v[1] + v[2]) / 2; });
    var mask = new Uint8Array(w * h), rows = new Array(h), cols = new Array(w), x, y;
    for (y = 0; y < h; y++) rows[y] = 0;
    for (x = 0; x < w; x++) cols[x] = 0;
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) {
      var c = px(x, y);
      if (Math.abs(c[0] - bg[0]) + Math.abs(c[1] - bg[1]) + Math.abs(c[2] - bg[2]) > 36) { mask[y * w + x] = 1; rows[y]++; cols[x]++; }
    }
    var t = -1, b = -1, l = -1, r = -1, k = Math.max(1, Math.round(w / 150));
    for (y = 0; y < h; y++) if (rows[y] > k) { if (t < 0) t = y; b = y; }
    for (x = 0; x < w; x++) if (cols[x] > k) { if (l < 0) l = x; r = x; }
    if (t < 0 || l < 0) return null;
    var ww = r - l + 1, hh = b - t + 1, asp = hh / ww;
    if (ww < 0.2 * w || hh < 0.4 * h || asp < 0.55 || asp > 4.5) return null;
    var ci = Math.round((l + r) / 2);
    if (Math.abs(ci - w / 2) > 0.06 * w) return null;
    // Runs of fabric across a row; gaps of up to g pixels are stepped over.
    function runs(yy, g) {
      var out = [], s = -1, e = -1, gap = 0;
      for (var xx = l; xx <= r; xx++) {
        if (mask[yy * w + xx]) { if (s < 0) s = xx; e = xx; gap = 0; }
        else if (s >= 0 && ++gap > g) { out.push([s, e]); s = -1; gap = 0; }
      }
      if (s >= 0) out.push([s, e]);
      return out;
    }
    var wl = [], gw = Math.max(1, Math.round(w / 120));
    for (y = Math.floor(t + hh * 0.02); y <= Math.floor(t + hh * 0.06); y++) { var rw = runs(y, gw); if (rw.length) wl.push(rw[rw.length - 1][1] - rw[0][0] + 1); }
    var waist = median(wl);
    if (!waist || waist < 0.5 * ww) return null;
    // The crotch: from the gap between the legs at the bottom of the photo, straight up the middle to the fabric. Legs
    // that touch lower down (baggy joggers, cuffs) are stepped over: where the gap opens again a little higher up, it
    // carries on from there.
    var cy = -1, xc, best = -1, skip = Math.round(hh * 0.08);
    for (xc = ci - 2; xc <= ci + 2; xc++) {
      var yy = b, top0 = -1;
      while (yy > t) {
        while (yy > t && !mask[yy * w + xc]) yy--;
        if (yy >= b - 2) break;
        top0 = yy;
        var y3 = yy;
        while (y3 > t && y3 > yy - skip && mask[y3 * w + xc]) y3--;
        if (y3 - 2 <= t || mask[y3 * w + xc] || mask[(y3 - 1) * w + xc] || mask[(y3 - 2) * w + xc]) break;
        yy = y3;
      }
      if (top0 >= 0 && (best < 0 || top0 < cy)) { cy = top0; best = xc; }
    }
    var pxcm = waist / waistCm, zw = areaW * pxcm, zh = areaH * pxcm, lg = null, approx = false, y2, rr, i;
    // The rise (waist to crotch) is about 0.7 of the flat waist width. A gap that only starts much lower means the legs
    // touch above it (baggy joggers); no gap at all means they touch all the way down (some shorts).
    if (cy >= 0 && cy - t >= 0.35 * waist && cy - t <= 1.3 * waist) {
      // Each leg a little below the crotch (no lower than the hem), split at the gap (a single stray pixel is stepped
      // over): the first row from 1.5 cm below the crotch where the gap is clear.
      y2 = Math.max(cy + 1, Math.min(b - 1, cy + Math.max(2, Math.round(1.5 * pxcm))));
      for (; y2 <= Math.min(b - 1, cy + 6 * pxcm) && !lg; y2++) {
        rr = runs(y2, 1);
        for (i = 0; i + 1 < rr.length; i++) if (rr[i][1] < best && rr[i + 1][0] > best) lg = [rr[i], rr[i + 1]];
      }
    }
    if (!lg) {
      // Crotch estimated from the waist; the legs are split at the middle. Only for a straight-on view: the product is as
      // wide there as at the waist and centred. Marked approximate.
      cy = Math.round(t + 0.7 * waist);
      y2 = Math.round(cy + 1.5 * pxcm);
      if (y2 + zh > b) return null;
      rr = runs(y2, gw);
      if (!rr.length) return null;
      var a0 = rr[0][0], e0 = rr[rr.length - 1][1];
      if (e0 - a0 + 1 < 0.85 * waist || Math.abs((a0 + e0) / 2 - ci) > 0.04 * ww) return null;
      lg = [[a0, ci - 1], [ci + 1, e0]];
      approx = true;
    }
    var L = lg[0], R = lg[1], lw = L[1] - L[0] + 1, rwd = R[1] - R[0] + 1;
    // Both legs about the same width and the same distance from the middle: a flat front view, not side-on or angled.
    if (Math.min(lw, rwd) < 0.7 * Math.max(lw, rwd)) return null;
    if (Math.abs((ci - (L[0] + L[1] + 1) / 2) - ((R[0] + R[1] + 1) / 2 - ci)) > 0.08 * ww) return null;
    var legW = (lw + rwd) / 2;
    if (legW < 0.25 * waist || legW > 0.8 * waist || zw > legW) return null;
    var top = cy + pxcm;
    if (top + zh > b + 1) top = b + 1 - zh - 0.5 * pxcm;
    if (top < t + hh * 0.1) return null;
    function zone(run) { return { zoneLeft: ((run[0] + run[1] + 1) / 2 - zw / 2) / w * 100, zoneTop: top / h * 100, zoneWidth: zw / w * 100 }; }
    var left = zone(R), right = zone(L);
    // How light the fabric is (0 dark – 255 light), sampled on the wearer's left leg; picks a text colour that shows up.
    var sum = 0, cnt = 0, sx = Math.round((R[0] + R[1]) / 2), sy = Math.round(top + zh / 2);
    for (var dy = -2; dy <= 2; dy++) for (var dx = -2; dx <= 2; dx++) {
      var cc = px(Math.min(w - 1, Math.max(0, sx + dx * 2)), Math.min(h - 1, Math.max(0, sy + dy * 2)));
      sum += 0.299 * cc[0] + 0.587 * cc[1] + 0.114 * cc[2]; cnt++;
    }
    return { zoneLeft: left.zoneLeft, zoneTop: left.zoneTop, zoneWidth: left.zoneWidth, tone: sum / cnt, approx: approx,
      box: { l: l / w * 100, t: t / h * 100, r: (r + 1) / w * 100, b: (b + 1) / h * 100 }, legs: { left: left, right: right } };
  }

  /*
   * Hi-vis tops (vests, tabards, polos, jackets): a left and a right chest print area on clear fabric, kept off the
   * reflective tape, the opening, ID holders, pockets and trims in another colour. The body is measured as for any
   * garment (garmentFit); its width stands for opts.bodyCm, which sets the cm scale. A pixel is clear fabric when its
   * colour is close to the garment's own colour on the chest (the median of the upper body). Each print area
   * (opts.areaW x opts.areaH cm) must be clear fabric with a 0.6 cm margin, on its own side of the centre line
   * (1.5 cm kept clear for the zip or fastening), in the upper half and above the tape band across the chest. The two
   * are level and mirrored (within 1 cm), as near the opening and as near the usual chest height (just below the neck)
   * as the tape allows. opts.sleeved / opts.longSleeve: tops with sleeves start at the shoulders, so a hood or collar
   * above isn't used (long sleeves are also measured as garmentFit's long-sleeve tops).
   * Returns garmentFit's result plus chest: { left, right } ({ zoneLeft, zoneTop, zoneWidth } in % of the photo;
   * left = the wearer's left chest, the right of the photo), or null when either can't be measured or there's no clear
   * place for the print areas.
   */
  function hivisFit(data, w, h, opts) {
    var g = garmentFit(data, w, h, { longSleeve: !!opts.longSleeve });
    if (!g) return null;
    function px(x, y) { var i = (y * w + x) * 4; return [data[i], data[i + 1], data[i + 2]]; }
    var cs = [px(2, 2), px(w - 3, 2), px(2, h - 3), px(w - 3, h - 3)];
    var bg = [0, 1, 2].map(function (k) { var v = cs.map(function (c) { return c[k]; }).sort(function (a, b) { return a - b; }); return (v[1] + v[2]) / 2; });
    function isFg(x, y) { var c = px(x, y); return Math.abs(c[0] - bg[0]) + Math.abs(c[1] - bg[1]) + Math.abs(c[2] - bg[2]) > 36; }
    var L = g.zoneLeft / 100 * w, W = g.zoneWidth / 100 * w, s = W / opts.bodyCm, cx = L + W / 2;
    var top = Math.round(g.box.t / 100 * h), bot = Math.round(g.box.b / 100 * h), gh = bot - top, x, y;
    var sm = [[], [], []];
    for (y = Math.floor(top + gh * 0.08); y < top + gh * 0.5; y += 2) for (x = Math.floor(L + W * 0.08); x < L + W * 0.92; x += 2) {
      if (!isFg(x, y)) continue;
      var c0 = px(x, y); sm[0].push(c0[0]); sm[1].push(c0[1]); sm[2].push(c0[2]);
    }
    if (sm[0].length < 50) return null;
    var dom = sm.map(function (a) { a.sort(function (p, q) { return p - q; }); return a[a.length >> 1]; });
    // Integral image of the pixels that aren't clear fabric (tape, trims, labels, the background).
    var ii = new Int32Array((w + 1) * (h + 1));
    for (y = 0; y < h; y++) {
      var row = 0;
      for (x = 0; x < w; x++) {
        var c = px(x, y);
        row += Math.abs(c[0] - dom[0]) + Math.abs(c[1] - dom[1]) + Math.abs(c[2] - dom[2]) > 70 ? 1 : 0;
        ii[(y + 1) * (w + 1) + x + 1] = ii[y * (w + 1) + x + 1] + row;
      }
    }
    function blocked(x0, y0, x1, y1) {
      if (x0 < 0 || y0 < 0 || x1 > w || y1 > h) return true;
      return ii[y1 * (w + 1) + x1] - ii[y0 * (w + 1) + x1] - ii[y1 * (w + 1) + x0] + ii[y0 * (w + 1) + x0] > 0;
    }
    var y0 = top;
    if (opts.sleeved || opts.longSleeve) for (var ys = top; ys < top + gh * 0.4; ys++) {
      var l2 = -1, r2 = -1;
      for (var xs = 0; xs < w; xs++) if (isFg(xs, ys)) { if (l2 < 0) l2 = xs; r2 = xs; }
      if (l2 >= 0 && r2 - l2 + 1 >= 0.9 * W) { y0 = ys; break; }
    }
    // The tape band across the chest: the first row from 30% of the garment height down (higher up, tape over the
    // shoulders or a yoke band doesn't count) where most of the garment across the body is light grey. Not on white or
    // grey garments, where the fabric itself looks like tape (the clear-fabric test still keeps prints off the tape).
    function tapeLike(c) { var mx = Math.max(c[0], c[1], c[2]), mn = Math.min(c[0], c[1], c[2]); return mx - mn < 45 && 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2] > 100; }
    var ylim = top + gh * 0.5;
    if (!tapeLike(dom)) for (var yb = Math.floor(top + gh * 0.3); yb < top + gh * 0.5; yb++) {
      var nf = 0, nb = 0;
      for (var xb = Math.floor(L); xb < L + W; xb++) if (isFg(xb, yb)) { nf++; if (tapeLike(px(xb, yb))) nb++; }
      if (nf > W * 0.5 && nb >= 0.5 * nf) { ylim = yb; break; }
    }
    var aw = Math.round(opts.areaW * s), ah = Math.round(opts.areaH * s), m = Math.max(1, Math.round(0.6 * s));
    var gap = 1.5 * s, ty = Math.round(top + gh * 0.1), tol = Math.max(1, Math.round(s)), best = null, maxO = Math.floor(W / 2 - aw);
    if (aw < 4 || maxO < gap) return null;
    function consider(yy, oL, oR) {
      var cost = (oL - gap) + (oR - gap) + (yy < ty ? 3 : 1) * Math.abs(yy - ty);
      if (!best || cost < best.cost) best = { cost: cost, y: yy, l: Math.round(cx + oL), r: Math.round(cx - oR) - aw };
    }
    for (var yy = Math.round(y0); yy + ah + m <= ylim; yy++) {
      // offsets from the centre line where each chest print area is clear
      var okL = [], okR = [], o, d;
      for (o = Math.ceil(gap); o <= maxO; o++) {
        var xl = Math.round(cx + o), xr = Math.round(cx - o);
        okL[o] = !blocked(xl - m, yy - m, xl + aw + m, yy + ah + m);
        okR[o] = !blocked(xr - aw - m, yy - m, xr + m, yy + ah + m);
      }
      // the nearest clear place to the opening on one side, with the other side's within 1 cm of its mirror
      for (o = Math.ceil(gap); o <= maxO; o++) if (okL[o]) {
        for (d = 0; d <= tol; d++) { if (okR[o + d]) { consider(yy, o, o + d); break; } if (okR[o - d]) { consider(yy, o, o - d); break; } }
        break;
      }
      for (o = Math.ceil(gap); o <= maxO; o++) if (okR[o]) {
        for (d = 0; d <= tol; d++) { if (okL[o + d]) { consider(yy, o + d, o); break; } if (okL[o - d]) { consider(yy, o - d, o); break; } }
        break;
      }
    }
    if (!best) return null;
    function zone(x0) { return { zoneLeft: x0 / w * 100, zoneTop: best.y / h * 100, zoneWidth: aw / w * 100 }; }
    return { zoneLeft: g.zoneLeft, zoneTop: g.zoneTop, zoneWidth: g.zoneWidth, tone: g.tone, box: g.box,
             chest: { left: zone(best.l), right: zone(best.r) } };
  }

  /*
   * Aprons (bib and waist aprons laid flat, photographed straight on). The apron is followed down the middle of the photo:
   * its top is the first row where the fabric across the middle is wide (the neck strap is a thin loop, so inside it the
   * middle is background); the full width is the median width over the lower part (the skirt). opts: scaleBy 'length'
   * (bib aprons, photographed as worn, so their sides wrap round: the length top of the bib to the hem, lengthCm, sets the
   * scale) or 'width' (waist aprons laid flat: the full width, widthCm, sets it), areaW, areaH (the print area in cm) and
   * belowCm (how far below the top the print area starts). The print area is centred on the apron at that height and must
   * fit across the fabric there with 0.5 cm to spare each side.
   * Returns the zone in % of the photo plus the apron box, or null.
   */
  function apronFit(data, w, h, opts) {
    function px(x, y) { var i = (y * w + x) * 4; return [data[i], data[i + 1], data[i + 2]]; }
    var cs = [px(2, 2), px(w - 3, 2), px(2, h - 3), px(w - 3, h - 3)];
    var bg = [0, 1, 2].map(function (k) { var v = cs.map(function (c) { return c[k]; }).sort(function (a, b) { return a - b; }); return (v[1] + v[2]) / 2; });
    function fg(x, y) { var c = px(x, y); return Math.abs(c[0] - bg[0]) + Math.abs(c[1] - bg[1]) + Math.abs(c[2] - bg[2]) > 36; }
    var cx = w >> 1, gapMax = Math.max(2, Math.round(w / 120)), runs = [], y;
    // The run of fabric through the middle of each row; small gaps (a seam, a pale stripe, a buckle) are stepped over.
    for (y = 0; y < h; y++) {
      if (!fg(cx, y) && !fg(cx - 1, y) && !fg(cx + 1, y)) { runs.push(null); continue; }
      var l = cx, r = cx, g = 0, x;
      for (x = cx - 1; x >= 0; x--) { if (fg(x, y)) { l = x; g = 0; } else if (++g > gapMax) break; }
      g = 0;
      for (x = cx + 1; x < w; x++) { if (fg(x, y)) { r = x; g = 0; } else if (++g > gapMax) break; }
      runs.push({ l: l, r: r });
    }
    // The top: wide fabric for at least 5% of the photo's height (the strap's top bar is thinner than that).
    var minW = 0.12 * w, top = -1, bot = -1, k, kk = Math.max(6, Math.round(h * 0.05));
    function wide(yy) { var q = runs[yy]; return q && q.r - q.l + 1 >= minW; }
    for (y = 0; y < h - kk && top < 0; y++) {
      if (!wide(y)) continue;
      for (k = 1; k <= kk && wide(y + k); k++);
      if (k > kk) top = y;
    }
    for (y = h - 1; y > top && bot < 0; y--) if (wide(y)) bot = y;
    if (top < 0 || bot - top < h * 0.25) return null;
    var ws = [];
    for (y = Math.round(top + (bot - top) * 0.6); y <= bot - (bot - top) * 0.05; y++) if (runs[y]) ws.push(runs[y].r - runs[y].l + 1);
    if (ws.length < 5) return null;
    ws.sort(function (a, b) { return a - b; });
    var fw = ws[ws.length >> 1], len = bot - top + 1;
    var s = opts.scaleBy === 'length' ? len / opts.lengthCm : fw / opts.widthCm;
    if (!(s > 0)) return null;
    // The apron touching the photo's sides means it's cropped (its hem may sit on the bottom edge: the photos are tight).
    var lo = w, hi = 0;
    for (y = top; y <= bot; y++) if (runs[y]) { lo = Math.min(lo, runs[y].l); hi = Math.max(hi, runs[y].r); }
    if (lo <= 1 || hi >= w - 2) return null;
    var aw = opts.areaW * s, ah = opts.areaH * s, zt = Math.round(top + (opts.belowCm || 0) * s);
    if (zt + ah > bot) return null;
    // Centred on the fabric at the print area's top and bottom rows, and it must fit across both with 1 cm to spare.
    var r0 = runs[zt], r1 = runs[Math.min(bot, Math.round(zt + ah))];
    if (!r0 || !r1) return null;
    var c0 = (Math.max(r0.l, r1.l) + Math.min(r0.r, r1.r)) / 2, room = Math.min(r0.r, r1.r) - Math.max(r0.l, r1.l) + 1;
    if (room < aw + s) return null;
    return { zoneLeft: (c0 - aw / 2) / w * 100, zoneTop: zt / h * 100, zoneWidth: aw / w * 100, apron: true,
             box: { l: lo / w * 100, t: top / h * 100, r: (hi + 1) / w * 100, b: (bot + 1) / h * 100 } };
  }

  // Measure a bag photo and place its print area (bagPlace); null when the body can't be found or its shape doesn't
  // match the bag size (more than 35% off; 60% for an angled photo, which is marked approx).
  // anyShape: the bag comes in several sizes and its photo may show any of them, so the shape isn't checked.
  function bagFit(data, w, h, bag, area, anyShape, vpos) {
    if (!bag || !area) return null;
    var body = bagBody(data, w, h);
    if (body.fail) return null;
    // A very light bag at an angle loses its edges against the white background, so its outline can't be trusted.
    if (body.approx && body.tone > 225) return null;
    var ratio = ((body.b - body.t) / (body.r - body.l)) / (bag.h / bag.w), tol = anyShape ? Infinity : body.approx ? 1.6 : 1.35;
    // A flat photo of a mid or dark bag that is taller than its stated size is still a good outline (a size that is only
    // an estimate, or a slight angle); a lost edge would make it shorter or off-centre instead.
    var taller = !body.approx && body.tone <= 225 && ratio > tol && ratio <= 1.6;
    if (ratio < 1 / tol || (ratio > tol && !taller)) return null;
    var out = bagPlace(body, w, h, bag, area, vpos);
    out.tone = body.tone;
    out.bag = true;
    out.approx = !!body.approx;
    return out;
  }

  /*
   * A chest print area beside the zip or placket, from a measured garment photo. fit: the body measurement (zoneLeft and
   * zoneWidth = the body, side seam to side seam, in % of the photo); bodyW: the body width in cm that stands for;
   * which: 'left' = the wearer's left chest (the right of a front photo), 'right' = the wearer's right; gap: cm kept clear
   * either side of the centre line (the zip); areaW: the chest print area width in cm. Same cm scale as the front.
   */
  function chestZone(fit, bodyW, which, gap, areaW) {
    var s = fit.zoneWidth / bodyW, centre = fit.zoneLeft + fit.zoneWidth / 2;
    var left = which === 'left' ? centre + gap * s : centre - (gap + areaW) * s;
    return { zoneLeft: left, zoneTop: fit.zoneTop, zoneWidth: areaW * s, box: fit.box };
  }

  // Bounds in cm (relative to the print area's top-left) of a box given in % of the photo.
  function pctBoxToCm(box, zone, areaW, stageAspect) {
    var cmX = areaW / zone.zoneWidth, cmY = cmX / stageAspect;
    return { x0: (box.l - zone.zoneLeft) * cmX, x1: (box.r - zone.zoneLeft) * cmX, y0: (box.t - zone.zoneTop) * cmY, y1: (box.b - zone.zoneTop) * cmY };
  }

  /* ----- Back view made from a front photo ----- */

  // Separable box blur (radius r) of a single-channel float image.
  function boxBlur(src, w, h, r) {
    var tmp = new Float32Array(w * h), out = new Float32Array(w * h), n = 2 * r + 1, x, y, k, s;
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        s = 0;
        for (k = -r; k <= r; k++) s += src[y * w + Math.min(w - 1, Math.max(0, x + k))];
        tmp[y * w + x] = s / n;
      }
    }
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        s = 0;
        for (k = -r; k <= r; k++) s += tmp[Math.min(h - 1, Math.max(0, y + k)) * w + x];
        out[y * w + x] = s / n;
      }
    }
    return out;
  }

  function median(arr) {
    if (!arr.length) return 0;
    var a = arr.slice().sort(function (p, q) { return p - q; }), m = a.length >> 1;
    return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
  }

  // Least squares for 3 unknowns (rows of [f0, f1, f2] -> y).
  function lsq3(rows, ys) {
    var A = [[0, 0, 0], [0, 0, 0], [0, 0, 0]], b = [0, 0, 0], i, j, k;
    for (i = 0; i < rows.length; i++) for (j = 0; j < 3; j++) { b[j] += rows[i][j] * ys[i]; for (k = 0; k < 3; k++) A[j][k] += rows[i][j] * rows[i][k]; }
    for (j = 0; j < 3; j++) A[j][j] += 1e-6;
    for (i = 0; i < 3; i++) {
      var piv = i;
      for (j = i + 1; j < 3; j++) if (Math.abs(A[j][i]) > Math.abs(A[piv][i])) piv = j;
      var t = A[i]; A[i] = A[piv]; A[piv] = t; var tb = b[i]; b[i] = b[piv]; b[piv] = tb;
      for (j = i + 1; j < 3; j++) { var f = A[j][i] / A[i][i]; for (k = i; k < 3; k++) A[j][k] -= f * A[i][k]; b[j] -= f * b[i]; }
    }
    var c = [0, 0, 0];
    for (i = 2; i >= 0; i--) { var sum = b[i]; for (k = i + 1; k < 3; k++) sum -= A[i][k] * c[k]; c[i] = sum / A[i][i]; }
    return c;
  }

  /* hand-over to the next part */
  var NS = root.__isgPz = { __parts: 0 };
  NS.money = money;
  NS.round1 = round1;
  NS.fitToTier = fitToTier;
  NS.clampBox = clampBox;
  NS.clampToBounds = clampToBounds;
  NS.stageBounds = stageBounds;
  NS.splitBody = splitBody;
  NS.garmentFit = garmentFit;
  NS.parseCmPair = parseCmPair;
  NS.parseSizedPairs = parseSizedPairs;
  NS.bagPlace = bagPlace;
  NS.endPlace = endPlace;
  NS.bagBody = bagBody;
  NS.headwearFit = headwearFit;
  NS.legFit = legFit;
  NS.hivisFit = hivisFit;
  NS.apronFit = apronFit;
  NS.bagFit = bagFit;
  NS.chestZone = chestZone;
  NS.pctBoxToCm = pctBoxToCm;
  NS.boxBlur = boxBlur;
  NS.median = median;
  NS.lsq3 = lsq3;
  NS.__parts = 1;
})(typeof window !== 'undefined' ? window : globalThis);
